import React, { ReactNode, Component, ErrorInfo } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state to trigger error UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can log the error to an error reporting service
    console.error('Error caught by error boundary:', error, errorInfo);
    this.setState({ error });
  }

  resetErrorBoundary = () => {
    // Reset the error state
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { hasError, error } = this.state;

    if (hasError) {
      return (
        <Dialog open={true} onClose={this.resetErrorBoundary}>
          <DialogContent>
            <Typography variant="h5" gutterBottom align="center">
              Oops! Something went wrong.
            </Typography>
            {error && (
              <>
                <Typography variant="body1" gutterBottom align="center">
                  Error Details:
                </Typography>
                <pre>{error.toString()}</pre>
              </>
            )}
            <Typography variant="body1" gutterBottom align="center">
              Please refresh the page or try again later.
            </Typography>
            <DialogActions sx={{ justifyContent: 'center' }}>
              <Button onClick={this.resetErrorBoundary} variant="contained" color="primary">
                Refresh
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
